import {
  OrganizationDTOOrganizationTypeIdEnum,
  OrganizationSettingsDTOAllowedTenancyTermTypesEnum,
  OrganizationSettingsDTOCommissionRuleEnum,
  OrganizationSettingsDTOMaximumPolicyCoverEnum,
  OrganizationSettingsDTOPricingRuleEnum,
  UserDTOInternalRolesEnum,
} from '@reposit/api-client';
import { get } from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { appConfig } from '../../appConfig';
import Button from '../../components/Button';
import Filter from '../../components/Filter';
import { HoverTooltip } from '../../components/HoverTooltip';
import IdentifierValue from '../../components/IdentifierValue';
import InlineEdit from '../../components/InlineEdit';
import InlineTable from '../../components/InlineTable';
import { FullPageLoader } from '../../components/Loading';
import { OrganizationInviteModal } from '../../components/OrganizationInviteModal';
import RepositCard from '../../components/Reposit/RepositCard';
import SecondaryPanel from '../../components/SecondaryPanel';
import { Header1, P1 } from '../../components/Typography';
import { useAdminDocumentTitle } from '../../hooks/use-document-title';
import { useIntegrators } from '../../hooks/use-integrators';
import { Option } from '../../interfaces/option.interface';
import { hasInternalRole } from '../../redux/account/account.selectors';
import { getInternalTeamMembersRequested } from '../../redux/internal-team-member/internal-team-member.actions';
import { createLoadingSelector } from '../../redux/loading/loading.selector';
import { setCurrentModal } from '../../redux/modal/modal.actions';
import { getCurrentModal } from '../../redux/modal/modal.selectors';
import {
  CREATE_ORGANIZATION_INVITE_STORE_KEY,
  FETCH_ORGANIZATION_BY_ID_STORE_KEY,
  fetchOrganizationByIdRequested,
} from '../../redux/organization/organization.actions';
import { getOrganizationById } from '../../redux/organization/organization.selectors';
import {
  FETCH_ORGANIZATION_INTERNAL_OWNERS_BY_ID_STORE_KEY,
  fetchOrganizationInternalOwnersByIdRequested,
} from '../../redux/organization-internal-owner/organization-internal-owner.actions';
import { fetchOrganizationRecipientRequested } from '../../redux/organization-recipient/organization-recipient.actions';
import { AppState } from '../../redux/root.reducer';
import { formatIntegratorOptions, getDisplayAddress } from '../../utils/common.utils';
import ClaimListContainer from '../Claim/List';
import OrganizationAddress from '../OrganisationAddress';
import OrganizationBankAccounts from '../OrganisationBankAccounts';
import OrganizationNotes from '../OrganisationNotes';
import { OrganizationAccount } from '../OrganizationAccount';
import OrganizationBranding from '../OrganizationBranding';
import OrganizationGroup from '../OrganizationGroup';
import OrganizationInvites from '../OrganizationInvites';
import OrganizationRecipients from '../OrganizationRecipients';
import OrganizationUsers from '../OrganizationUsers';
import OrganizationVerticals from '../OrganizationVerticals';
import RepositListContainer from '../Reposit/List';
import EditCommissionRateModal, { CommissionRuleLabels } from './EditCommissionRateModal';
import EditHubspotIdModal from './EditHubspotIdModal';
import EditInventoryConfirmationDelayModal from './EditInventoryConfirmationDelayModal';
import EditOrganizationNameModal from './EditOrganizationNameModal';
import EditPolicyCoverCapModal, { PolicyCoverCapLabels } from './EditPolicyCoverCapModal';
import EditPricingFeeModal, { PricingRuleLabels } from './EditPricingFeeModal';
import { formatInventoryConfirmationTiming, getInventoryConfirmationHours } from './inventory-confirmation-timing';

interface OrganisationProps {
  match: any;
}

interface OrgDetailsItem {
  key: string;
  value: string | JSX.Element;
}

const Address = styled(P1)`
  align-items: center;
  display: flex;
`;

const ActionButton = styled(Button)`
  margin-left: 10px;
`;

const PageHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const PricingFeeValue = styled.span`
  display: inline;
`;

const HubspotLink = styled.a`
  color: inherit;
  text-decoration: underline;
`;

const renderContent = (currentListShowing: string, orgId: string, integratorOptions: Option[]): JSX.Element | null => {
  switch (currentListShowing) {
    case 'REPOSITS':
      return <RepositListContainer organizationId={orgId} />;

    case 'CLAIMS':
      return <ClaimListContainer organizationId={orgId} />;

    case 'USERS':
      return <OrganizationUsers organizationId={orgId} integratorOptions={integratorOptions} />;

    case 'INVITES':
      return <OrganizationInvites organizationId={orgId} />;

    case 'RECIPIENTS':
      return <OrganizationRecipients organizationId={orgId} />;
    case 'NOTES':
      return <OrganizationNotes organizationId={orgId} />;
    case 'ADDRESS':
      return <OrganizationAddress organizationId={orgId} />;
    case 'BANK ACCOUNTS':
      return <OrganizationBankAccounts organizationId={orgId} />;
    case 'ACCOUNT':
      return <OrganizationAccount organizationId={orgId} />;
    case 'BRANDING':
      return <OrganizationBranding organizationId={orgId} />;
    case 'VERTICALS':
      return <OrganizationVerticals organizationId={orgId} />;
    case 'PARENTS':
      return <OrganizationGroup organizationId={orgId} />;
    default:
      return null;
  }
};

const CREATE_ORGANIZATION_INVITE_MODAL_TYPE = 'CREATE_ORGANIZATION_INVITE';
const EDIT_PRICING_FEE_MODAL_TYPE = 'EDIT_PRICING_FEE';
const EDIT_POLICY_COVER_CAP_MODAL_TYPE = 'EDIT_POLICY_COVER_CAP';
const EDIT_COMMISSION_RATE_MODAL_TYPE = 'EDIT_COMMISSION_RATE';
const EDIT_HUBSPOT_ID_MODAL_TYPE = 'EDIT_HUBSPOT_ID';
const EDIT_ORGANIZATION_NAME_MODAL_TYPE = 'EDIT_ORGANIZATION_NAME';
const EDIT_INVENTORY_CONFIRMATION_DELAY_MODAL_TYPE = 'EDIT_INVENTORY_CONFIRMATION_DELAY';
const UPDATE_ORGANIZATION_PRICING_RULE_ROLE = UserDTOInternalRolesEnum.UPDATEORGANIZATIONPRICINGRULE;
const UPDATE_ORGANIZATION_POLICY_COVER_CAP_ROLE = UserDTOInternalRolesEnum.UPDATEORGANIZATIONPOLICYCOVERCAP;
const UPDATE_ORGANIZATION_COMMISSION_RULE_ROLE = UserDTOInternalRolesEnum.UPDATEORGANIZATIONCOMMISSIONRULE;
const UPDATE_ORGANIZATION_HUBSPOT_ID_ROLE = UserDTOInternalRolesEnum.UPDATEORGANIZATIONHUBSPOTID;
const UPDATE_ORGANIZATION_NAME_ROLE = UserDTOInternalRolesEnum.UPDATEORGANIZATIONNAME;
const UPDATE_ORGANIZATION_INVENTORY_CONFIRMATION_DELAY_ROLE =
  UserDTOInternalRolesEnum.UPDATEORGANIZATIONINVENTORYCONFIRMATIONDELAY;

const getHubspotCompanyUrl = (hubspotId: string) => {
  if (!appConfig.hubspotPortalId) return undefined;

  return `https://app.hubspot.com/contacts/${encodeURIComponent(appConfig.hubspotPortalId)}/company/${encodeURIComponent(
    hubspotId,
  )}`;
};

const allOptions = [
  'REPOSITS',
  'CLAIMS',
  'USERS',
  'INVITES',
  'RECIPIENTS',
  'NOTES',
  'ADDRESS',
  'ACCOUNT',
  'BRANDING',
  'VERTICALS',
  'PARENTS',
  'BANK ACCOUNTS',
];

const STANDARD_CLAIM_SUBMISSION_DEADLINE_DAYS = 24;
const EXTENDED_CLAIM_SUBMISSION_DEADLINE_DAYS = 29;

export const getClaimSubmissionDurationDays = (autoExtendOutcomeDeadlineEnabled: boolean): number =>
  autoExtendOutcomeDeadlineEnabled ? EXTENDED_CLAIM_SUBMISSION_DEADLINE_DAYS : STANDARD_CLAIM_SUBMISSION_DEADLINE_DAYS;

const DEFAULT_CLAIM_SUBMISSION_DURATION_TOOLTIP = 'Default. Auto-extend is off, so this partner has 24 days to submit charges.';
const EXTENDED_CLAIM_SUBMISSION_DURATION_TOOLTIP =
  'Extended. Auto-extend is on, so this partner has 29 days: the standard 24-day window plus the 5-day extension.';

export const getClaimSubmissionDurationTooltip = (autoExtendOutcomeDeadlineEnabled: boolean): string =>
  autoExtendOutcomeDeadlineEnabled ? EXTENDED_CLAIM_SUBMISSION_DURATION_TOOLTIP : DEFAULT_CLAIM_SUBMISSION_DURATION_TOOLTIP;

export const getOptions = (
  hasOrganizationBankAccountsRole: boolean,
  isPrivateOrganization: boolean,
  hasManageOrganizationGroupsRole: boolean,
): string[] => {
  let options = allOptions;

  if (isPrivateOrganization || !hasManageOrganizationGroupsRole) {
    options = options.filter((option) => option !== 'PARENTS');
  }

  if (!hasOrganizationBankAccountsRole) {
    options = options.filter((option) => option !== 'BANK ACCOUNTS');
  }

  return options;
};

const ReferrerBadge = styled.div`
  background: ${(props) => props.theme.colors.positive};
  border-radius: 24px;
  display: inline-block;
  color: #fff;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 1em;
  font-weight: bold;
  margin: 0 0 0 auto;
  padding: 6px 20px;
  margin-bottom: 6px;
`;

const Organisation: React.FC<OrganisationProps> = ({ match }) => {
  const dispatch = useDispatch();
  const { isLoading: areIntegratorsLoading, integrators } = useIntegrators();
  const integratorOptions = formatIntegratorOptions(integrators);
  const [currentListShowing, setCurrentListShowing] = useState('REPOSITS');
  const orgId = match.params.id;
  useAdminDocumentTitle(orgId);
  const organization = useSelector((state: AppState) => getOrganizationById(state, orgId));

  const organizationLoadingSelector = createLoadingSelector([
    FETCH_ORGANIZATION_BY_ID_STORE_KEY,
    FETCH_ORGANIZATION_INTERNAL_OWNERS_BY_ID_STORE_KEY,
  ]);
  const isOrgLoading = useSelector(organizationLoadingSelector);
  const currentModal = useSelector(getCurrentModal);

  const inviteSendingSelector = createLoadingSelector([CREATE_ORGANIZATION_INVITE_STORE_KEY]);
  const isInviteLoading = useSelector(inviteSendingSelector);

  const hasBankAccountsRole = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.FINANCEINDIVIDUAL),
  );
  const hasManageGroupsRole = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.MANAGEORGANIZATIONGROUPS),
  );
  const hasManageAccountManagersRole = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.MANAGEORGANIZATIONACCOUNTMANAGER),
  );
  const hasUpdateOrganizationPricingRuleRole = useSelector((state: AppState) =>
    hasInternalRole(state, UPDATE_ORGANIZATION_PRICING_RULE_ROLE),
  );
  const hasUpdateOrganizationPolicyCoverCapRole = useSelector((state: AppState) =>
    hasInternalRole(state, UPDATE_ORGANIZATION_POLICY_COVER_CAP_ROLE),
  );
  const hasUpdateOrganizationCommissionRuleRole = useSelector((state: AppState) =>
    hasInternalRole(state, UPDATE_ORGANIZATION_COMMISSION_RULE_ROLE),
  );
  const hasUpdateOrganizationHubspotIdRole = useSelector((state: AppState) =>
    hasInternalRole(state, UPDATE_ORGANIZATION_HUBSPOT_ID_ROLE),
  );
  const hasUpdateOrganizationNameRole = useSelector((state: AppState) => hasInternalRole(state, UPDATE_ORGANIZATION_NAME_ROLE));
  const hasUpdateOrganizationInventoryConfirmationDelayRole = useSelector((state: AppState) =>
    hasInternalRole(state, UPDATE_ORGANIZATION_INVENTORY_CONFIRMATION_DELAY_ROLE),
  );
  const isLoading = isOrgLoading || areIntegratorsLoading || !integrators.length;
  useEffect(() => {
    dispatch(fetchOrganizationByIdRequested(orgId));
    dispatch(fetchOrganizationRecipientRequested({ organizationId: orgId }));
    dispatch(fetchOrganizationInternalOwnersByIdRequested(orgId));
    if (hasManageAccountManagersRole) {
      dispatch(getInternalTeamMembersRequested());
    }
    return function resetOrganizationInviteModal() {
      dispatch(setCurrentModal(''));
    };
  }, [dispatch, hasManageAccountManagersRole, orgId]);

  if (isLoading || !organization) return <FullPageLoader />;

  const { name, attributes, legacyId, settings } = organization;
  const hubspotId = get(attributes, 'hubspotId', '');
  const referrerIntegratorId = get(attributes, 'referrerIntegratorId', '');
  const referrerIntegrator = referrerIntegratorId ? integrators.find((i) => i.id === referrerIntegratorId) : undefined;
  const referrerIntegratorName = get(referrerIntegrator, 'name', '');
  const offerRepositEnabled = get(settings, 'offerRepositEnabled', false);
  const pricingRule = get(settings, 'pricingRule', undefined);
  const commissionRule = get(settings, 'commissionRule', undefined);
  const annualFeesEnabled = get(settings, 'topUpEnabled', true);
  const autoExtendOutcomeDeadlineEnabled = get(settings, 'autoExtendOutcomeDeadlineEnabled', false);
  const claimSubmissionDurationDays = getClaimSubmissionDurationDays(autoExtendOutcomeDeadlineEnabled);
  const claimSubmissionDurationTooltip = getClaimSubmissionDurationTooltip(autoExtendOutcomeDeadlineEnabled);
  const policyCoverCap = get(settings, 'maximumPolicyCover', OrganizationSettingsDTOMaximumPolicyCoverEnum.FIVETHOUSANDPOUNDS);
  const delayedInventoryConfirmationEnabled = get(settings, 'delayedInventoryConfirmationEnabled', false);
  const inventoryConfirmationTiming = formatInventoryConfirmationTiming(
    getInventoryConfirmationHours(delayedInventoryConfirmationEnabled),
  );
  const inventoryConfirmationTimingTooltip =
    'New inventories, and confirmations rebuilt when a start date changes, use this timing. Confirmations already scheduled keep their current date.';
  const allowedTenancyTermTypes: OrganizationSettingsDTOAllowedTenancyTermTypesEnum[] = get(
    settings,
    'allowedTenancyTermTypes',
    [],
  );
  const organizationGroupName = get(organization, 'organizationGroup.name', 'Not set');
  const parentOrganizationGroupName = get(organization, 'parentOrganizationGroup.name', 'Not set');
  const isPrivateOrganization = organization.organizationTypeId === OrganizationDTOOrganizationTypeIdEnum.PRIVATE;
  const pricingRuleLabels: PricingRuleLabels = {
    [OrganizationSettingsDTOPricingRuleEnum.STANDARD]: 'Standard',
    [OrganizationSettingsDTOPricingRuleEnum.STANDARDCAP250]: 'Reposit fee capped at £250',
    [OrganizationSettingsDTOPricingRuleEnum.STANDARDCAP300]: 'Reposit fee capped at £300',
    [OrganizationSettingsDTOPricingRuleEnum.STANDARDCAP350]: 'Reposit fee capped at £350',
    [OrganizationSettingsDTOPricingRuleEnum.NOMINALPERTENANT]: 'Nominal per tenant (£1)',
    [OrganizationSettingsDTOPricingRuleEnum.STANDARD15PERCENTDISCOUNT]: 'Standard 15% discount',
  };
  const pricingRuleValue = pricingRule ? pricingRuleLabels[pricingRule] : 'N/A';

  const commissionRuleLabels: CommissionRuleLabels = {
    [OrganizationSettingsDTOCommissionRuleEnum.FEEPERCENTAGE125]: '125%',
    [OrganizationSettingsDTOCommissionRuleEnum.FEEPERCENTAGE31POINT75]: '31.75%',
    [OrganizationSettingsDTOCommissionRuleEnum.FEEPERCENTAGE30]: '30%',
    [OrganizationSettingsDTOCommissionRuleEnum.FEEPERCENTAGE25]: '25%',
    [OrganizationSettingsDTOCommissionRuleEnum.FEEPERCENTAGE20]: '20%',
    [OrganizationSettingsDTOCommissionRuleEnum.FEEPERCENTAGE17POINT5]: '17.5%',
    [OrganizationSettingsDTOCommissionRuleEnum.FEEPERCENTAGE16]: '16%',
    [OrganizationSettingsDTOCommissionRuleEnum.FEEPERCENTAGE15]: '15%',
    [OrganizationSettingsDTOCommissionRuleEnum.FEEPERCENTAGE12POINT5]: '12.5%',
    [OrganizationSettingsDTOCommissionRuleEnum.FEEPERCENTAGE10]: '10%',
    [OrganizationSettingsDTOCommissionRuleEnum.FEEPERCENTAGE5]: '5%',
    [OrganizationSettingsDTOCommissionRuleEnum.FIXED25]: 'Fixed 25',
    [OrganizationSettingsDTOCommissionRuleEnum.PPMPERCENTAGE6]: '6% PPM',
    [OrganizationSettingsDTOCommissionRuleEnum.PPMPERCENTAGE6POINT5]: '6.5% PPM',
    [OrganizationSettingsDTOCommissionRuleEnum.NOCOMMISSION]: 'No commission',
  };

  const policyCoverCapLabels: PolicyCoverCapLabels = {
    [OrganizationSettingsDTOMaximumPolicyCoverEnum.FIVETHOUSANDPOUNDS]: '£5,000',
    [OrganizationSettingsDTOMaximumPolicyCoverEnum.TENTHOUSANDPOUNDS]: '£10,000',
  };

  const tenancyTermTypeLabels = {
    [OrganizationSettingsDTOAllowedTenancyTermTypesEnum.FIXED]: 'Fixed',
    [OrganizationSettingsDTOAllowedTenancyTermTypesEnum.PERIODIC]: 'Periodic',
  };

  const groupDetailItems: OrgDetailsItem[] = isPrivateOrganization
    ? []
    : [
        { key: 'Parent', value: organizationGroupName },
        { key: 'Ultimate Parent', value: parentOrganizationGroupName },
      ];

  const orgDetailsItems: OrgDetailsItem[] = [
    { key: 'Organisation ID', value: <IdentifierValue>{organization.id}</IdentifierValue> },
    ...groupDetailItems,
    { key: 'Offer Reposit Enabled', value: offerRepositEnabled ? 'Yes' : 'No' },
    { key: 'Cash Deposit Scheme Enabled', value: organization.hasCashDepositEnabled ? 'Yes' : 'No' },
    {
      key: 'Pricing Fee',
      value: (
        <PricingFeeValue>
          {pricingRuleValue}
          {hasUpdateOrganizationPricingRuleRole ? (
            <InlineEdit onClick={() => dispatch(setCurrentModal(EDIT_PRICING_FEE_MODAL_TYPE))} />
          ) : null}
        </PricingFeeValue>
      ),
    },
    {
      key: 'Commission Rate',
      value: (
        <PricingFeeValue>
          {commissionRule ? commissionRuleLabels[commissionRule] : 'N/A'}
          {hasUpdateOrganizationCommissionRuleRole ? (
            <InlineEdit onClick={() => dispatch(setCurrentModal(EDIT_COMMISSION_RATE_MODAL_TYPE))} />
          ) : null}
        </PricingFeeValue>
      ),
    },
    { key: 'Annual Fees Enabled', value: annualFeesEnabled ? 'Yes' : 'No' },
    {
      key: 'Claim Submission Duration',
      value: <HoverTooltip tooltip={claimSubmissionDurationTooltip}>{`${claimSubmissionDurationDays} days`}</HoverTooltip>,
    },
    {
      key: 'Policy Cover Cap',
      value: (
        <PricingFeeValue>
          {policyCoverCapLabels[policyCoverCap]}
          {hasUpdateOrganizationPolicyCoverCapRole ? (
            <InlineEdit onClick={() => dispatch(setCurrentModal(EDIT_POLICY_COVER_CAP_MODAL_TYPE))} />
          ) : null}
        </PricingFeeValue>
      ),
    },
    {
      key: 'Allowed Tenancy Terms',
      value: allowedTenancyTermTypes.length
        ? allowedTenancyTermTypes.map((type) => tenancyTermTypeLabels[type]).join(', ')
        : 'N/A',
    },
    {
      key: 'Inventory confirmation',
      value: (
        <PricingFeeValue>
          <HoverTooltip tooltip={inventoryConfirmationTimingTooltip}>{inventoryConfirmationTiming}</HoverTooltip>
          {hasUpdateOrganizationInventoryConfirmationDelayRole ? (
            <InlineEdit onClick={() => dispatch(setCurrentModal(EDIT_INVENTORY_CONFIRMATION_DELAY_MODAL_TYPE))} />
          ) : null}
        </PricingFeeValue>
      ),
    },
  ];
  if (legacyId) orgDetailsItems.push({ key: 'Legacy ID', value: <IdentifierValue>{legacyId}</IdentifierValue> });
  const hubspotCompanyUrl = hubspotId ? getHubspotCompanyUrl(hubspotId) : undefined;
  orgDetailsItems.push({
    key: 'HubSpot ID',
    value: (
      <PricingFeeValue>
        {hubspotId && hubspotCompanyUrl ? (
          <HubspotLink href={hubspotCompanyUrl} rel="noopener noreferrer" target="_blank">
            <IdentifierValue>{hubspotId}</IdentifierValue>
          </HubspotLink>
        ) : hubspotId ? (
          <IdentifierValue>{hubspotId}</IdentifierValue>
        ) : (
          'Not set'
        )}
        {hasUpdateOrganizationHubspotIdRole ? (
          <InlineEdit onClick={() => dispatch(setCurrentModal(EDIT_HUBSPOT_ID_MODAL_TYPE))} />
        ) : null}
      </PricingFeeValue>
    ),
  });

  return (
    <Fragment>
      {currentModal === CREATE_ORGANIZATION_INVITE_MODAL_TYPE ? (
        <OrganizationInviteModal
          onDismiss={() => dispatch(setCurrentModal(''))}
          organizationId={orgId}
          integratorOptions={integratorOptions}
          isInviteLoading={isInviteLoading}
        />
      ) : null}
      {currentModal === EDIT_PRICING_FEE_MODAL_TYPE && hasUpdateOrganizationPricingRuleRole ? (
        <EditPricingFeeModal
          onDismiss={() => dispatch(setCurrentModal(''))}
          organizationId={orgId}
          policyCoverCap={policyCoverCap}
          pricingRule={pricingRule}
          pricingRuleLabels={pricingRuleLabels}
        />
      ) : null}
      {currentModal === EDIT_POLICY_COVER_CAP_MODAL_TYPE && hasUpdateOrganizationPolicyCoverCapRole ? (
        <EditPolicyCoverCapModal
          onDismiss={() => dispatch(setCurrentModal(''))}
          organizationId={orgId}
          policyCoverCap={policyCoverCap}
          policyCoverCapLabels={policyCoverCapLabels}
          pricingRule={pricingRule}
        />
      ) : null}
      {currentModal === EDIT_COMMISSION_RATE_MODAL_TYPE && hasUpdateOrganizationCommissionRuleRole ? (
        <EditCommissionRateModal
          onDismiss={() => dispatch(setCurrentModal(''))}
          organizationId={orgId}
          commissionRule={commissionRule}
          commissionRuleLabels={commissionRuleLabels}
        />
      ) : null}
      {currentModal === EDIT_HUBSPOT_ID_MODAL_TYPE && hasUpdateOrganizationHubspotIdRole ? (
        <EditHubspotIdModal onDismiss={() => dispatch(setCurrentModal(''))} organizationId={orgId} hubspotId={hubspotId} />
      ) : null}
      {currentModal === EDIT_ORGANIZATION_NAME_MODAL_TYPE && hasUpdateOrganizationNameRole ? (
        <EditOrganizationNameModal onDismiss={() => dispatch(setCurrentModal(''))} organizationId={orgId} name={name} />
      ) : null}
      {currentModal === EDIT_INVENTORY_CONFIRMATION_DELAY_MODAL_TYPE && hasUpdateOrganizationInventoryConfirmationDelayRole ? (
        <EditInventoryConfirmationDelayModal
          onDismiss={() => dispatch(setCurrentModal(''))}
          organizationId={orgId}
          delayedInventoryConfirmationEnabled={delayedInventoryConfirmationEnabled}
        />
      ) : null}
      <Container>
        <Row>
          <Col sm={12}>
            <PageHeaderContainer>
              <Header1>{name}</Header1>
              {hasUpdateOrganizationNameRole ? (
                <InlineEdit onClick={() => dispatch(setCurrentModal(EDIT_ORGANIZATION_NAME_MODAL_TYPE))} />
              ) : null}
              {referrerIntegratorName ? <ReferrerBadge>{referrerIntegratorName}</ReferrerBadge> : null}
            </PageHeaderContainer>
            {attributes && attributes.address && <Address>{getDisplayAddress(attributes.address)}</Address>}
          </Col>
        </Row>
        {/* For now, only displaying the table if the legacy id is filled */}
        {orgDetailsItems.length ? (
          <Row>
            <Col lg={12}>
              <RepositCard title="Organisation Details">
                <Container>
                  <Row>
                    <Col lg={12} style={{ padding: 0 }}>
                      <InlineTable items={orgDetailsItems} />
                    </Col>
                  </Row>
                </Container>
              </RepositCard>
            </Col>
          </Row>
        ) : null}
      </Container>
      <Container style={{ marginBottom: 24 }}>
        <Row>
          <Col lg={12}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
              <ActionButton onClick={() => dispatch(setCurrentModal(CREATE_ORGANIZATION_INVITE_MODAL_TYPE))}>
                Invite user
              </ActionButton>
            </div>
            <SecondaryPanel style={{ background: '#E7E4F1' }}>
              <Filter
                label="Showing"
                onSelect={setCurrentListShowing}
                selected={currentListShowing}
                options={getOptions(hasBankAccountsRole, isPrivateOrganization, hasManageGroupsRole)}
              />
            </SecondaryPanel>
          </Col>
        </Row>
      </Container>
      <div style={{ padding: 24 }}>{renderContent(currentListShowing, orgId, integratorOptions)}</div>
    </Fragment>
  );
};

export default Organisation;
